/**
 * API Utilities
 *
 * Provides a fetch wrapper with CSRF protection, error handling,
 * and common request patterns.
 */

import { getToken, getHeaders } from './csrf.js';

/**
 * Default error handler
 * @param {Error} error - The error to handle
 */
function defaultErrorHandler(error) {
    console.error('API Error:', error);
}

/**
 * Make a fetch request with CSRF protection and JSON handling
 *
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object} options.data - Request body data (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.includeToken - Whether to include CSRF token in body (default: true)
 * @returns {Promise<Object>} The JSON response
 */
export async function request(url, options = {}) {
    const {
        method = 'GET',
        data = null,
        headers = {},
        includeToken = true
    } = options;

    const fetchOptions = {
        method,
        headers: getHeaders(headers)
    };

    // Add body for non-GET requests
    if (method !== 'GET' && data !== null) {
        const bodyData = includeToken ? { ...data, _token: getToken() } : data;
        fetchOptions.body = JSON.stringify(bodyData);
    }

    const response = await fetch(url, fetchOptions);

    // Try to parse JSON response
    let jsonResponse;
    try {
        jsonResponse = await response.json();
    } catch (e) {
        // If response is not JSON, create a basic response object
        jsonResponse = {
            success: response.ok,
            status: response.status,
            message: response.ok ? 'Success' : 'Request failed'
        };
    }

    // If response is not ok, throw an error with the message
    if (!response.ok) {
        const error = new Error(jsonResponse.message || `HTTP ${response.status}`);
        error.response = jsonResponse;
        error.status = response.status;
        throw error;
    }

    return jsonResponse;
}

/**
 * GET request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} The JSON response
 */
export function get(url, options = {}) {
    return request(url, { ...options, method: 'GET' });
}

/**
 * POST request
 * @param {string} url - The URL to post to
 * @param {Object} data - The data to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} The JSON response
 */
export function post(url, data = {}, options = {}) {
    return request(url, { ...options, method: 'POST', data });
}

/**
 * PUT request
 * @param {string} url - The URL to put to
 * @param {Object} data - The data to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} The JSON response
 */
export function put(url, data = {}, options = {}) {
    return request(url, { ...options, method: 'PUT', data });
}

/**
 * DELETE request
 * @param {string} url - The URL to delete
 * @param {Object} data - Optional data to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} The JSON response
 */
export function del(url, data = {}, options = {}) {
    return request(url, { ...options, method: 'DELETE', data });
}

/**
 * Submit a form-style DELETE request (creates a form and submits it)
 * For when you need a full page reload after delete
 *
 * @param {string} url - The URL to delete
 * @param {string} confirmMessage - Optional confirmation message
 * @returns {boolean} False if cancelled, true if submitted
 */
export function submitDelete(url, confirmMessage) {
    if (confirmMessage && !confirm(confirmMessage)) {
        return false;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'DELETE';
    form.appendChild(methodInput);

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = '_token';
    tokenInput.value = getToken();
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
    return true;
}

/**
 * Submit a form with a custom HTTP method (PUT, PATCH, DELETE)
 * For when you need a full page reload after the action
 *
 * @param {string} method - The HTTP method
 * @param {string} url - The URL to submit to
 * @param {Object} data - Additional form data
 * @param {string} confirmMessage - Optional confirmation message
 * @returns {boolean} False if cancelled, true if submitted
 */
export function submitWithMethod(method, url, data = {}, confirmMessage) {
    if (confirmMessage && !confirm(confirmMessage)) {
        return false;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    // Add method override
    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = method.toUpperCase();
    form.appendChild(methodInput);

    // Add CSRF token
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = '_token';
    tokenInput.value = getToken();
    form.appendChild(tokenInput);

    // Add additional data
    if (data && typeof data === 'object') {
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = data[key];
                form.appendChild(input);
            }
        }
    }

    document.body.appendChild(form);
    form.submit();
    return true;
}

// Export as default object for easier importing
export default {
    request,
    get,
    post,
    put,
    del,
    submitDelete,
    submitWithMethod
};
